/**
 * Custom hook for managing export templates
 * Handles loading, saving, and CRUD operations for templates
 */

import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { updateGameInfo } from '@/lib/stores/gameStore';
import { ExportTemplate, ExportTemplatesConfig } from '@/types/exportTemplates';
import { getPresetTemplates, createCustomFromPreset, getDatabasePresets } from '@/lib/integrations/exportTemplatePresets';

interface UseTemplateManagementProps {
  selectedGame: any;
}

export const useTemplateManagement = ({ selectedGame }: UseTemplateManagementProps) => {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<ExportTemplate[]>([]);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<ExportTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load templates from game settings
  useEffect(() => {
    if (!selectedGame) return;
    
    // Get all available presets
    const codePresets = getPresetTemplates();
    const databasePresets = getDatabasePresets();
    const allPresets = [...codePresets, ...databasePresets];
    
    const config = selectedGame.settings?.exportTemplates as ExportTemplatesConfig | undefined;
    
    if (config && config.templates && config.templates.length > 0) {
      // Deprecated preset IDs that should be filtered out
      const deprecatedPresetIds = ['preset-unity-database'];
      
      // Load saved templates, filter out deprecated ones, and ensure functions have all required fields
      const templatesWithDefaults = config.templates
        .filter(template => !deprecatedPresetIds.includes(template.id))
        .map(template => ({
          ...template,
          functions: template.functions?.map(func => ({
            ...func,
            variables: func.variables || [],
            commonVariables: func.commonVariables || []
          })) || []
        }));
      
      // Merge with any new presets that aren't already in the saved templates
      const savedTemplateIds = new Set(templatesWithDefaults.map(t => t.id));
      const missingPresets = allPresets.filter(preset => !savedTemplateIds.has(preset.id));
      
      // Combine saved templates with any missing presets
      const mergedTemplates = [...templatesWithDefaults, ...missingPresets];
      
      setTemplates(mergedTemplates);
      // Handle case where active template was deprecated
      const activeId = deprecatedPresetIds.includes(config.activeTemplateId || '') 
        ? mergedTemplates[0]?.id || null 
        : config.activeTemplateId || null;
      setActiveTemplateId(activeId);
      // Select first available template (handle case where previously selected was deprecated)
      const firstAvailableId = mergedTemplates[0]?.id || null;
      setSelectedTemplateId(firstAvailableId);
    } else {
      // Initialize with all presets (code generation + database)
      setTemplates(allPresets);
      setActiveTemplateId(allPresets[0].id);
      setSelectedTemplateId(allPresets[0].id);
    }
  }, [selectedGame]);

  // Save templates to game settings
  const saveTemplates = async () => {
    if (!selectedGame) return;
    
    setIsSaving(true);
    
    try {
      // Only save the active template for this game
      const activeTemplate = templates.find(t => t.id === activeTemplateId);
      
      if (!activeTemplate) {
        toast({
          title: 'Error',
          description: 'No active template selected',
          variant: 'destructive'
        });
        setIsSaving(false);
        return;
      }
      
      // Ensure functions data is properly structured before saving
      const sanitizedTemplate = {
        ...activeTemplate,
        functions: activeTemplate.functions?.map(func => ({
          name: func.name,
          description: func.description || '',
          content: func.content,
          appliesTo: func.appliesTo,
          variables: func.variables || [],
          commonVariables: func.commonVariables || []
        })) || []
      };
      
      const config: ExportTemplatesConfig = {
        templates: [sanitizedTemplate],
        activeTemplateId
      };
      
      console.log('💾 Saving active export template to backend:', {
        gameId: selectedGame.id,
        templateId: activeTemplate.id,
        templateName: activeTemplate.name,
        config
      });
      
      const result = await updateGameInfo(
        selectedGame.id,
        {
          settings: {
            ...selectedGame.settings,
            exportTemplates: config
          }
        },
        toast
      );
      
      console.log('✅ Export template saved successfully:', result);
      
      toast({
        title: 'Template Saved',
        description: `Active export template (${activeTemplate.name}) has been saved successfully`
      });
    } catch (error) {
      console.error('❌ Failed to save export templates:', error);
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save templates',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Set active template
  const handleSetActive = (templateId: string) => {
    setActiveTemplateId(templateId);
    toast({
      title: 'Active Template Changed',
      description: 'Don\'t forget to save your changes'
    });
  };

  // Create new custom template
  const handleCreateCustom = () => {
    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
    if (!selectedTemplate) return;
    
    const newTemplate = createCustomFromPreset(
      selectedTemplate,
      `Custom ${selectedTemplate.name}`
    );
    
    setTemplates([...templates, newTemplate]);
    setSelectedTemplateId(newTemplate.id);
    setEditingTemplate(newTemplate);
    setIsEditing(true);
  };

  // Duplicate template
  const handleDuplicate = (template: ExportTemplate) => {
    const duplicatedTemplate = createCustomFromPreset(
      template,
      `${template.name} (Copy)`
    );
    
    setTemplates([...templates, duplicatedTemplate]);
    setSelectedTemplateId(duplicatedTemplate.id);
    
    toast({
      title: 'Template Duplicated',
      description: 'Template has been duplicated. Don\'t forget to save your changes.'
    });
  };

  // Edit template (creates copy if preset)
  const handleEdit = (template: ExportTemplate) => {
    if (template.isPreset) {
      // Create a custom copy from the preset
      const customTemplate = createCustomFromPreset(
        template,
        `Custom ${template.name}`
      );
      
      setTemplates([...templates, customTemplate]);
      setSelectedTemplateId(customTemplate.id);
      setEditingTemplate(customTemplate);
      setIsEditing(true);
      
      toast({
        title: 'Editing Copy',
        description: 'Created a custom copy of the preset template for editing.'
      });
      return;
    }
    
    setEditingTemplate({ ...template });
    setIsEditing(true);
  };

  // Save edited template
  const handleSaveEdit = () => {
    if (!editingTemplate) return;
    
    setTemplates(templates.map(t => 
      t.id === editingTemplate.id ? editingTemplate : t
    ));
    
    setIsEditing(false);
    setEditingTemplate(null);
    
    toast({
      title: 'Template Updated',
      description: 'Don\'t forget to save your changes'
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingTemplate(null);
  };

  // Delete custom template
  const handleDelete = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    
    if (!template) return;
    
    if (template.isPreset) {
      toast({
        title: 'Cannot Delete Preset',
        description: 'Preset templates cannot be deleted',
        variant: 'destructive'
      });
      return;
    }
    
    if (activeTemplateId === templateId) {
      toast({
        title: 'Cannot Delete Active Template',
        description: 'Please set another template as active first',
        variant: 'destructive'
      });
      return;
    }
    
    setTemplates(templates.filter(t => t.id !== templateId));
    
    if (selectedTemplateId === templateId) {
      setSelectedTemplateId(templates[0]?.id || null);
    }
    
    toast({
      title: 'Template Deleted',
      description: 'Don\'t forget to save your changes'
    });
  };

  return {
    templates,
    setTemplates,
    activeTemplateId,
    setActiveTemplateId,
    selectedTemplateId,
    setSelectedTemplateId,
    editingTemplate,
    setEditingTemplate,
    isEditing,
    setIsEditing,
    isSaving,
    saveTemplates,
    handleSetActive,
    handleCreateCustom,
    handleDuplicate,
    handleEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleDelete
  };
};
